// Aktenuebernahme MVP AP5 (20.10.2026): reine Anzeige-/Pruefhilfen fuer das Pruef-Panel. Die fachliche Wahrheit (Status, Sperren,
// Entscheidungen) liegt ausschliesslich serverseitig; diese Funktionen verhindern nur offensichtlich unzulaessige Eingaben vorab.

export const UI_STATUS_LABEL = {
  eingelesen: 'Eingelesen',
  analysiert: 'Analysiert',
  pruefpflichtig: 'Prüfpflichtig',
  gesperrt: 'Gesperrt',
  fehler: 'Fehler',
  abgeschlossen: 'Abgeschlossen',
};

export const UI_STATUS_FARBE = {
  eingelesen: 'bg-slate-700 text-slate-200',
  analysiert: 'bg-sky-900 text-sky-200',
  pruefpflichtig: 'bg-amber-900 text-amber-200',
  gesperrt: 'bg-red-900 text-red-200',
  fehler: 'bg-rose-950 text-rose-300',
  abgeschlossen: 'bg-emerald-900 text-emerald-200',
};

export const TYP_REITER = [
  { typ: 'aufgabe', label: 'Aufgaben' },
  { typ: 'frist', label: 'Fristen' },
  { typ: 'vorgang', label: 'Vorgänge' },
  { typ: 'konto', label: 'Konten' },
  { typ: 'schuld', label: 'Schulden' },
  { typ: 'stammdaten', label: 'Stammdaten' },
];

export const SPERRGRUND_TEXT = {
  konflikt: 'Widerspruch zum Bestand – bitte klären',
  konflikt_veraendert: 'Bestand hat sich seit dem Vorschlag geändert – neu prüfen',
  klient_unklar: 'Klientenzuordnung nicht gesichert',
  unvollstaendig: 'Analyse unvollständig – Inhalt prüfen',
  feld_nicht_zulaessig: 'Feld darf nicht vorgeschlagen werden',
  typ_unbekannt: 'Unbekannte Art',
  gate_fehler: 'Prüf-Gate nicht erreichbar',
};

export const TRENN_SPERRGRUND_TEXT = {
  sperre_mehrere_klienten: 'Seiten mehrerer Klienten',
  sperre_identitaetskonflikt: 'Widersprüchliche Identität innerhalb eines Schreibens',
  sperre_identitaet_unklar: 'Schreiben ohne erkennbaren Klienten',
  sperre_unvollstaendig: 'Seiten nicht vollständig lesbar',
  anderer_klient: 'Dokument gehört erkennbar einem anderen Klienten',
  briefanfang_ohne_identitaet: 'Weiteres Schreiben ohne erkennbare Identität',
};

export function bestandHinweis(v) {
  const b = v && v.bestand;
  if (!b) return null;
  switch (b.art) {
    case 'aufgabe': return `Bereits vorhanden: offene Aufgabe ${String(b.id || '').slice(0, 8)}`;
    case 'offener_pruefvorschlag': return `Bereits als offener Prüfvorschlag vorhanden (${String(b.id || '').slice(0, 8)})`;
    case 'frist': return `Bereits vorhanden: Frist ${String(b.id || '').slice(0, 8)}`;
    case 'konto': return 'Konto bereits erfasst';
    case 'schuld': return 'Gläubiger bereits erfasst';
    case 'vorgang': return 'Vorgang bereits vorhanden';
    case 'stammdaten_gleich': return 'Stammdatum stimmt mit dem Bestand überein';
    case 'stammdaten_konflikt': return `Bestand: „${b.bestand_wert ?? ''}“`;
    case 'stammdaten_leer': return 'Feld im Bestand leer – wird nur nach Freigabe ergänzt';
    default: return null;
  }
}

// Vorbelegung des Ziels einer Einheit: NUR der eigene Klient des Jobs (wenn die Seitenanalyse ihn erkannt hat). Fremde nie.
export function zielVorbelegung(einheit, jobKlientId) {
  return einheit && einheit.vorschlag_ziel && einheit.vorschlag_ziel === jobKlientId ? jobKlientId : '';
}

// Lueckenlos, ueberschneidungsfrei, jede Seite 1..n genau einmal, jedes Ziel gewaehlt. -> Fehlertext oder null.
export function trennungPruefen(einheiten, seitenzahl) {
  if (!Array.isArray(einheiten) || einheiten.length === 0) return 'Mindestens ein Seitenbereich erforderlich';
  const gesehen = new Array(seitenzahl + 1).fill(0);
  for (const e of einheiten) {
    const von = Number(e.von), bis = Number(e.bis);
    if (!Number.isInteger(von) || !Number.isInteger(bis) || von < 1 || bis < von || bis > seitenzahl) return `Ungültiger Bereich ${e.von}–${e.bis}`;
    if (!e.klient_id) return `Für Seiten ${von}–${bis} ist kein Ziel gewählt`;
    for (let s = von; s <= bis; s += 1) gesehen[s] += 1;
  }
  const doppelt = gesehen.findIndex((n, i) => i > 0 && n > 1);
  if (doppelt > 0) return `Seite ${doppelt} ist mehrfach zugeordnet`;
  const fehlt = gesehen.findIndex((n, i) => i > 0 && n === 0);
  if (fehlt > 0) return `Seite ${fehlt} ist keinem Teil zugeordnet`;
  return null;
}

// Sammelentscheidung nur fuer eindeutig Zusammengehoeriges (gleicher Klient/Typ/Vorgangsart, alle offen) -- wie serverseitig.
export function sammelErlaubt(vorschlaege) {
  if (!Array.isArray(vorschlaege) || vorschlaege.length < 2) return false;
  const k = vorschlaege[0].sammel_schluessel;
  return Boolean(k) && vorschlaege.every((v) => v.status === 'offen' && v.sammel_schluessel === k);
}

export function nachTyp(vorschlaege) {
  const out = {};
  for (const r of TYP_REITER) out[r.typ] = [];
  for (const v of vorschlaege || []) (out[v.typ] = out[v.typ] || []).push(v);
  return out;
}

export function offenZahl(vorschlaege) {
  return (vorschlaege || []).filter((v) => ['offen', 'gesperrt', 'wird_angewendet'].includes(v.status)).length;
}

// Darf der Bestaetigungs-Knopf erscheinen? Nur abgelegte Dokumente ohne offene Entscheidung (Server prueft erneut).
export function bestaetigbar(datei) {
  return Boolean(datei) && datei.status === 'abgelegt' && !datei.pruefung_bestaetigt && (datei.entscheidungen_offen || 0) === 0;
}


// AP6a (20.10.2026): Gate-Status verstaendlich anzeigen. Die gespeicherten Werte des bestehenden Pruef-Gates (Altbezeichnung
// "CHATGPT_*") bleiben unveraendert -- es wird KEIN ChatGPT aufgerufen; nur die Anzeige wird uebersetzt.
const GATE_STATUS_LABEL = {
  PENDING_CHATGPT_REVIEW: 'Prüfung offen',
  CHATGPT_APPROVED: 'freigegeben',
  CHATGPT_REJECTED: 'abgelehnt',
  CHATGPT_MODIFIED: 'geändert freigegeben',
  HUMAN_REVIEW_REQUIRED: 'manuelle Prüfung nötig',
};
const WRITE_PHASE_LABEL = { AUSSTEHEND: 'Übernahme läuft', ERLEDIGT: 'übernommen', FEHLGESCHLAGEN: 'Übernahme fehlgeschlagen' };

export function gateLabel(gate) {
  if (!gate) return '';
  const s = GATE_STATUS_LABEL[gate.status] || 'Status unbekannt';
  const w = gate.write_phase ? WRITE_PHASE_LABEL[gate.write_phase] || 'Schreibstatus unbekannt' : '';
  return w ? `${s} – ${w}` : s;
}

// Vorauswahl: gemerkter Job, sonst der juengste URSPRUNGSJOB (kein Teil-Job aus einer Trennung), sonst der juengste Job.
export function vorwahlJob(jobs, gemerktId) {
  const js = jobs || [];
  if (gemerktId && js.some((j) => j.id === gemerktId)) return gemerktId;
  return (js.find((j) => !j.nur_explizite_dateien) || js[0] || {}).id || null;
}
